import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';

function SelectPlan() {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponError, setCouponError] = useState('');
  
  const navigate = useNavigate();

  const plans = [
    {
      id: 'standard',
      name: 'Standard',
      price: 30,
      screens: 1,
      features: [
        '1 screen',
        'Multi-layout (5-6 zones)',
        'Synced video playback',
        'Full exercise video library',
        'Equipment-based filtering',
        'Content scheduling',
        'Request new exercises'
      ]
    },
    {
      id: 'standard_plus',
      name: 'Standard Plus',
      price: 60,
      screens: 2,
      popular: true,
      features: [
        '2 screens',
        'Multi-layout on each screen',
        'Synced video playback',
        'Full exercise video library',
        'Equipment-based filtering',
        'Content scheduling',
        'Request new exercises'
      ]
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 99,
      screens: 5,
      features: [
        'Up to 5 screens',
        'Advanced custom layouts',
        'Synced video playback',
        'Full exercise video library',
        'Equipment-based filtering',
        'Content scheduling',
        'Priority exercise requests'
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 149,
      screens: 15,
      features: [
        'Up to 15 screens',
        'Advanced custom layouts',
        'Multi-location support',
        'Synced video playback',
        'Full exercise video library',
        'Equipment-based filtering',
        'Priority exercise requests'
      ]
    }
  ];

  const handleApplyCoupon = () => {
    setCouponError('');
    
    // TODO: Validate coupon against Stripe or Firestore
    // For now, just store it to pass to payment page
    if (couponCode.trim()) {
      setCouponApplied(true);
    }
  };

  const handleContinue = () => {
    if (!selectedPlan) return;
    
    // Store selection and navigate to payment
    sessionStorage.setItem('selectedPlan', selectedPlan);
    if (couponCode.trim()) {
      sessionStorage.setItem('couponCode', couponCode.trim());
    }
    
    navigate('/payment');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8fafc',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
    }}>
      {/* Header */}
      <div style={{
        background: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        padding: '20px 40px'
      }}>
        <img 
          src="/pulse_blue.png" 
          alt="Pulse" 
          style={{ height: '36px' }}
        />
      </div>

      {/* Content */}
      <div style={{
        maxWidth: '1100px',
        margin: '0 auto',
        padding: '32px 40px'
      }}>
        {/* Title */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h1 style={{
            fontSize: '32px',
            fontWeight: 700,
            color: '#1e293b',
            margin: '0 0 12px 0'
          }}>
            Choose your plan
          </h1>
          <p style={{
            fontSize: '16px',
            color: '#64748b',
            margin: 0
          }}>
            Select the plan that works best for your gym
          </p>
        </div>

        {/* Plan Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '24px',
          marginBottom: '40px'
        }}>
          {plans.map((plan) => (
            <div
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              style={{
                background: '#ffffff',
                borderRadius: '16px',
                padding: '32px',
                cursor: 'pointer',
                border: selectedPlan === plan.id 
                  ? '2px solid #E63946' 
                  : '2px solid #e2e8f0',
                boxShadow: selectedPlan === plan.id
                  ? '0 4px 20px rgba(230, 57, 70, 0.15)'
                  : '0 1px 3px rgba(0,0,0,0.05)',
                transition: 'all 0.2s ease',
                position: 'relative'
              }}
            >
              {/* Selected Indicator */}
              {selectedPlan === plan.id && (
                <div style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  width: '24px',
                  height: '24px',
                  background: '#E63946',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Check size={14} color="#ffffff" />
                </div>
              )}

              {/* Plan Name */}
              <h3 style={{
                fontSize: '20px',
                fontWeight: 600,
                color: '#1e293b',
                margin: '0 0 8px 0'
              }}>
                {plan.name}
              </h3>

              {/* Price */}
              <div style={{ marginBottom: '24px' }}>
                <span style={{
                  fontSize: '42px',
                  fontWeight: 700,
                  color: '#1e293b'
                }}>
                  ${plan.price}
                </span>
                <span style={{
                  fontSize: '16px',
                  color: '#64748b'
                }}>
                  /month
                </span>
              </div>

              {/* Features */}
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {plan.features.map((feature, index) => (
                  <li 
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '8px 0',
                      fontSize: '15px',
                      color: '#475569'
                    }}
                  >
                    <Check size={18} color="#10b981" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Coupon Section */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '32px',
          border: '1px solid #e2e8f0'
        }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: 500,
            color: '#374151',
            marginBottom: '8px'
          }}>
            Have a coupon code?
          </label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <input
              type="text"
              value={couponCode}
              onChange={(e) => {
                setCouponCode(e.target.value.toUpperCase());
                setCouponApplied(false);
                setCouponError('');
              }}
              placeholder="Enter code"
              style={{
                flex: 1,
                padding: '12px 16px',
                fontSize: '15px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                outline: 'none'
              }}
            />
            <button
              onClick={handleApplyCoupon}
              disabled={!couponCode.trim()}
              style={{
                padding: '12px 24px',
                fontSize: '15px',
                fontWeight: 500,
                color: couponCode.trim() ? '#E63946' : '#94a3b8',
                background: '#ffffff',
                border: couponCode.trim() ? '1px solid #E63946' : '1px solid #e2e8f0',
                borderRadius: '8px',
                cursor: couponCode.trim() ? 'pointer' : 'default'
              }}
            >
              Apply
            </button>
          </div>
          {couponApplied && (
            <p style={{ 
              color: '#10b981', 
              fontSize: '14px', 
              marginTop: '8px',
              marginBottom: 0 
            }}>
              Coupon will be applied at checkout
            </p>
          )}
          {couponError && (
            <p style={{ 
              color: '#dc2626', 
              fontSize: '14px', 
              marginTop: '8px',
              marginBottom: 0 
            }}>
              {couponError}
            </p>
          )}
        </div>

        {/* Continue Button */}
        <button
          onClick={handleContinue}
          disabled={!selectedPlan}
          style={{
            width: '100%',
            padding: '16px 24px',
            fontSize: '16px',
            fontWeight: 600,
            color: '#ffffff',
            background: selectedPlan ? '#E63946' : '#cbd5e1',
            border: 'none',
            borderRadius: '10px',
            cursor: selectedPlan ? 'pointer' : 'default',
            transition: 'background 0.2s'
          }}
        >
          Continue to Payment
        </button>
      </div>
    </div>
  );
}

export default SelectPlan;